import {
  CButton,
  CButtonGroup,
  CCard,
  CCardBody,
  CCardHeader,
  CCol,
  CDataTable,
  CPagination,
  CRow,
  CLabel,
  CInputGroup,
  CInputGroupText,
  CInput,
  CBadge,
} from "@coreui/react";
import { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { getList } from "../../api/generics";
import EmailGroup from "../../models/email-groups";
import { getRoutePath } from "../../routes";
import { SUCCESS } from "../../utils/constants/tags";
import { successAlert, warningAlert } from "../utils/messages";
import { BsFillPlusCircleFill } from "react-icons/bs";
import EmailGroupUpdateModal from "./EmailGroupUpdateModal";
import EmailGroupDeleteModal from "./EmailGroupDeleteModal";
import { useSelector } from "react-redux";
import { RootState } from "../../store";
import PermissionRequiredComponent from "../permissions/PermissionRequiredComponent";
import {
  EMAIL_GROUPS_CREATE,
  EMAIL_GROUPS_DESTROY,
  EMAIL_GROUPS_UPDATE,
} from "../../auth/permissions";

const ITEMS_PER_PAGE = 10;

const EmailGroupList = () => {
  const fields = [
    {
      key: "name",
      _classes: ["text-center"],
      label: "Nombre",
    },
    {
      key: "active",
      _classes: ["text-center"],
      label: "Estado",
    },
    {
      key: "actions",
      _classes: ["text-center"],
      label: "Acciones",
      filter: false,
    },
  ];

  const [search, setSearch] = useState<string>("");

  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [emailGroup, setEmailGroup] = useState<EmailGroup | undefined>(
    undefined
  );
  const [loading, setLoading] = useState(true);
  const [emailgroups, setEmailGroups] = useState<EmailGroup[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const history = useHistory();
  const companyId = useSelector(
    (state: RootState) => state.company.data.company.id
  );

  const getURLParams = () => {
    let urlParams = new URLSearchParams(history.location.search);
    let page = urlParams.get("page")
      ? parseInt(urlParams.get("page") as string)
      : 1;

    if (page <= 0) {
      page = 1;
    }
    let search = urlParams.get("search") ? urlParams.get("search") : "";
    let map = new Map();
    map.set("search", search);
    map.set("page", page);
    return map;
  };

  const fetchEmailGroups = async () => {
    const urlParams = getURLParams();
    const limit = ITEMS_PER_PAGE;
    const offset = ITEMS_PER_PAGE * Number(urlParams.get("page") - 1);
    urlParams.set("company_id", companyId);
    const emailgroupsStatus = await getList<EmailGroup>(
      "/email_groups/",
      limit,
      offset,
      urlParams
    );
    if (emailgroupsStatus.status === SUCCESS) {
      if (emailgroupsStatus.data !== undefined) {
        const count = emailgroupsStatus.data.count;
        const pages = Math.ceil((count ? count : 0) / ITEMS_PER_PAGE);
        setTotalPages(pages);
        setEmailGroups(emailgroupsStatus.data.items);
        setCurrentPage(urlParams.get("page"));
      }
    } else {
      const message = emailgroupsStatus.detail
        ? emailgroupsStatus.detail
        : "Error desconocido";
      warningAlert(message);
    }
    setLoading(false);
  };

  const onPageChange = (page: number) => {
    setLoading(true);
    let urlParams = new URLSearchParams(history.location.search);
    let previousParams = getURLParams();
    urlParams.set(
      "search",
      previousParams.get("search") ? previousParams.get("search") : ""
    );
    urlParams.set("page", page.toString());
    let url = `?${urlParams.toString()}`;
    history.push(url);
  };

  const onAddClick = () => {
    setShowEditModal(true);
  };

  const onUpdateClick = (emailgroup: EmailGroup) => {
    setEmailGroup(emailgroup);
    setShowEditModal(true);
  };

  const onDeleteClick = (emailgroup: EmailGroup) => {
    setEmailGroup(emailgroup);
    setShowDeleteModal(true);
  };

  const onEmailGroupEditCancel = () => {
    setEmailGroup(undefined);
    setShowEditModal(false);
  };

  const onEmailGroupEditSuccess = () => {
    successAlert("Datos guardados con éxito!");
    setShowEditModal(false);
    setEmailGroups([]);
    setLoading(true);
    fetchEmailGroups();
  };

  const onEmailGroupDeleteCancel = () => {
    setEmailGroup(undefined);
    setShowDeleteModal(false);
  };

  const onEmailGroupDeleteSuccess = () => {
    successAlert("Registro eliminado con éxito!");
    setShowDeleteModal(false);
    setEmailGroups([]);
    setLoading(true);
    fetchEmailGroups();
  };

  const onSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
  };

  const onSearch = () => {
    setLoading(true);
    let urlParams = new URLSearchParams(history.location.search);
    urlParams.set("search", search);
    let url = `?${urlParams.toString()}`;
    history.push(url);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      onSearch();
    }
  };

  useEffect(() => {
    fetchEmailGroups();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    return history.listen((location) => {
      if (location.pathname !== getRoutePath("emailGroups")) {
        return;
      }
      fetchEmailGroups();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history]);

  return (
    <>
      <CRow>
        <CCol lg="12">
          <CCard>
            <CCardHeader className="d-flex flex-row mb-3">
              <div className="p-2 ">
                <h3>Grupos de Correo</h3>
              </div>
              <PermissionRequiredComponent permissionName={EMAIL_GROUPS_CREATE}>
                <div className="p-2 ">
                  <CButton
                    color="primary"
                    className="float-right"
                    onClick={onAddClick}
                  >
                    <BsFillPlusCircleFill />
                    &nbsp; Agregar Nuevo
                  </CButton>
                </div>
              </PermissionRequiredComponent>
            </CCardHeader>
            <CCardBody>
              <CRow>
                <CCol>
                  <CLabel
                    className="visually-hidden"
                    htmlFor="autoSizingInputGroup"
                  ></CLabel>
                  <CInputGroup>
                    <CInputGroupText>
                      <i className="fa fa-search"></i>
                    </CInputGroupText>
                    <CInput
                      type="text"
                      placeholder="Introduzca el termino de busqueda"
                      onChange={onSearchChange}
                      value={search}
                      onKeyDown={handleKeyDown}
                    />
                    <CButton
                      type="button"
                      color="primary"
                      variant="outline"
                      onClick={onSearch}
                    >
                      Buscar
                    </CButton>
                  </CInputGroup>
                </CCol>
              </CRow>
              <div>
                <CDataTable
                  noItemsView={<h2 className="text-center">Sin Resultados</h2>}
                  addTableClasses={"table-fixed"}
                  fields={fields}
                  items={emailgroups}
                  striped
                  border
                  loading={loading}
                  responsive
                  scopedSlots={{
                    active: (item: EmailGroup) => {
                      return (
                        <td className="text-center">
                          <CBadge color={item.active ? "success" : "secondary"}>
                            {item.active ? "Productor" : "Inproductor"}
                          </CBadge>
                        </td>
                      );
                    },
                    actions: (item: EmailGroup) => {
                      const editButton = (
                        <CButton
                          className="text-white"
                          color="warning"
                          onClick={() => {
                            onUpdateClick(item);
                          }}
                        >
                          <i className="fa fa-pencil"></i>
                        </CButton>
                      );
                      const deleteButton = (
                        <CButton
                          className="text-white"
                          color="danger"
                          onClick={() => {
                            onDeleteClick(item);
                          }}
                        >
                          <i className="fa fa-trash"></i>
                        </CButton>
                      );

                      return (
                        <td className="text-right">
                          <CButtonGroup>
                            <PermissionRequiredComponent
                              permissionName={EMAIL_GROUPS_UPDATE}
                            >
                              {editButton}
                            </PermissionRequiredComponent>
                            <PermissionRequiredComponent
                              permissionName={EMAIL_GROUPS_DESTROY}
                            >
                              {deleteButton}
                            </PermissionRequiredComponent>
                          </CButtonGroup>
                        </td>
                      );
                    },
                  }}
                />
                <div className="d-flex justify-content-center">
                  <CPagination
                    pages={totalPages}
                    activePage={currentPage}
                    onActivePageChange={(i: number) => onPageChange(i)}
                    className={totalPages < 2 ? "d-none" : ""}
                  />
                </div>
              </div>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>
      <EmailGroupUpdateModal
        show={showEditModal}
        onCancel={onEmailGroupEditCancel}
        onSuccess={onEmailGroupEditSuccess}
        emailGroup={emailGroup}
      />
      <EmailGroupDeleteModal
        show={showDeleteModal}
        onCancel={onEmailGroupDeleteCancel}
        onSuccess={onEmailGroupDeleteSuccess}
        emailGroup={emailGroup}
      />
    </>
  );
};

export default EmailGroupList;
